import React from 'react';
import { EnumUserRoute } from '@components-member/userRoutes/enums';
import { userRoute } from '@components-member/userRoutes/userRoute';
import ENUM_CONSTANTS from '@components-shared/enum.constants';
import { FormattedDateTime } from '@components-shared/formattedDateTime';
import { IColumn } from '@components-shared/shared.interfaces';
import { keyMap } from '@locales/lokalise/keyMap';
import { getBooleanValue, getFormattedNumber, getLink, getTableLabel, getValueFromEnums } from '@utils/helper';
import { IContextProps, ISalesUserTradingAccount } from './interfaces';

export const SALES_USER_TRADING_ACCOUNT_GRID_COLUMNS = [
  {
    key: 'id',
    name: keyMap.sharedFieldsId,
    render: (row: ISalesUserTradingAccount, value: string) =>
      getLink(value, userRoute(EnumUserRoute.salesUserTradingAccountDetails).getPath({ id: row.id })),
  },
  {
    key: 'createdOn',
    name: keyMap.sharedFieldsCreatedOn,
    render: (row: ISalesUserTradingAccount, value: string) => {
      return <FormattedDateTime value={value} />;
    },
  },
  {
    key: 'login',
    name: keyMap.sharedFieldsTradingAccount,
  },
  {
    key: 'user.fullName',
    name: keyMap.sharedFieldsUser,
    render: (row: ISalesUserTradingAccount, value: string) =>
      getLink(value, userRoute(EnumUserRoute.salesUserDetails).getPath({ id: row.user.id })),
    isHidden: (ctx: IContextProps) => ctx.props.userId,
  },
  {
    key: 'user.email',
    name: keyMap.sharedFieldsEmail,
    isHidden: (ctx: IContextProps) => ctx.props.userId,
  },
  {
    key: 'createdOn',
    name: keyMap.sharedFieldsCreatedOn,
    render: (row: ISalesUserTradingAccount, value: string) => {
      return <FormattedDateTime value={value} />;
    },
  },
  {
    key: 'accountType',
    name: keyMap.sharedFieldsAccountType,
    render: (row: ISalesUserTradingAccount, value: string) =>
      getTableLabel(value, ENUM_CONSTANTS.TRADING_ACCOUNT_TYPE_ACCOUNT),
  },
  {
    key: 'platformType',
    name: keyMap.sharedFieldsTradingPlatformType,
    render: (row: ISalesUserTradingAccount, value: string) =>
      getValueFromEnums(value, ENUM_CONSTANTS.TRADING_PLATFORM_TYPE),
  },
  {
    key: 'tradingServer',
    name: keyMap.sharedFieldsTradingServer,
  },
  {
    key: 'typeName',
    name: keyMap.sharedFieldsType,
  },
  {
    key: 'clientSourceType',
    name: keyMap.sharedFieldsClientSourceType,
    render: (row: ISalesUserTradingAccount, value: string) =>
      getValueFromEnums(value, ENUM_CONSTANTS.CLIENT_SOURCE_TYPE),
  },
  {
    key: 'agentCode',
    name: keyMap.sharedFieldsParentIbCode,
  },
  {
    key: 'currency',
    name: keyMap.sharedFieldsCurrency,
    render: (row: ISalesUserTradingAccount, value: string) => getValueFromEnums(value, ENUM_CONSTANTS.PSP_CURRENCY),
  },
  {
    key: 'balance',
    name: keyMap.commonBalance,
    render: (row: ISalesUserTradingAccount, value: number) => getFormattedNumber(value, { precision: true }),
  },
  {
    key: 'credit',
    name: keyMap.commonCredit,
    render: (row: ISalesUserTradingAccount, value: number) => getFormattedNumber(value, { precision: true }),
  },
  {
    key: 'equity',
    name: keyMap.commonEquity,
    render: (row: ISalesUserTradingAccount, value: number) => getFormattedNumber(value, { precision: true }),
  },
  {
    key: 'margin',
    name: keyMap.commonMargin,
    render: (row: ISalesUserTradingAccount, value: number) => getFormattedNumber(value, { precision: true }),
  },
  {
    key: 'marginFree',
    name: keyMap.commonFreeMargin,
    render: (row: ISalesUserTradingAccount, value: number) => getFormattedNumber(value, { precision: true }),
  },
  {
    key: 'leverage',
    name: keyMap.commonLeverage,
  },
  {
    key: 'enabled',
    name: keyMap.sharedFieldsEnabled,
    render: (row: ISalesUserTradingAccount, value: boolean) => getBooleanValue(value),
  },
  {
    key: 'readOnly',
    name: keyMap.salesFieldsTradingAccountIsReadOnly,
    render: (row: ISalesUserTradingAccount, value: boolean) => getBooleanValue(value),
  },
  {
    key: 'lastDepositActionOn',
    name: keyMap.salesFieldsTradingAccountLastDeposit,
    render: (row: ISalesUserTradingAccount, value: string) => {
      return <FormattedDateTime value={value} />;
    },
  },
  {
    key: 'lastLoginOn',
    name: keyMap.salesFieldsTradingAccountLastLogin,
    render: (row: ISalesUserTradingAccount, value: string) => {
      return <FormattedDateTime value={value} />;
    },
  },
];

export const SALES_USER_TRADING_ACCOUNT_GRID_FILTERS_FIELDS = [
  {
    name: 'createdOn',
    title: keyMap.sharedFieldsCreatedOn,
    type: 'dateTimeRange',
  },
  {
    name: 'user',
    title: keyMap.sharedFieldsUser,
    type: 'input',
    isHidden: (ctx: IContextProps) => !!ctx.props.userId,
  },
  {
    name: 'login',
    title: keyMap.sharedFieldsTradingAccount,
  },
  {
    name: 'accountType',
    title: keyMap.sharedFieldsAccountType,
    type: 'select',
    options: ENUM_CONSTANTS.TRADING_ACCOUNT_TYPE_ACCOUNT,
  },
  {
    name: 'enabled',
    title: keyMap.sharedFieldsEnabled,
    type: 'select',
    options: ENUM_CONSTANTS.YES_NO,
  },
  {
    name: 'userId',
    type: 'hidden',
    defaultValue: (ctx: IContextProps) => ctx.props.userId,
  },
];

export const SALES_USER_TRADING_ACCOUNT_GRID_ACTIONS = [
  {
    name: keyMap.sharedActionsDetails,
    type: 'link',
    link: EnumUserRoute.salesUserTradingAccounts,
  },
];

export const SALES_USER_TRADING_ACCOUNT_DETAILS_COLUMNS: IColumn[] = [
  {
    key: 'id',
    name: keyMap.sharedFieldsId,
  },
  {
    key: 'createdOn',
    name: keyMap.sharedFieldsCreatedOn,
    render: (data: ISalesUserTradingAccount, value: string): JSX.Element => {
      return <FormattedDateTime value={value} />;
    },
  },
  {
    key: 'login',
    name: keyMap.sharedFieldsTradingAccount,
  },
  {
    key: 'user.fullName',
    name: keyMap.sharedFieldsUser,
    render: (row: ISalesUserTradingAccount, value: string) =>
      getLink(value, userRoute(EnumUserRoute.salesUserDetails).getPath({ id: row.user.id })),
  },
  {
    key: 'user.email',
    name: keyMap.sharedFieldsEmail,
  },
  {
    key: 'accountType',
    name: keyMap.sharedFieldsAccountType,
    render: (row: ISalesUserTradingAccount, value: string) =>
      getTableLabel(value, ENUM_CONSTANTS.TRADING_ACCOUNT_TYPE_ACCOUNT),
  },
  {
    key: 'platformType',
    name: keyMap.sharedFieldsTradingPlatformType,
    render: (row: ISalesUserTradingAccount, value: string) =>
      getValueFromEnums(value, ENUM_CONSTANTS.TRADING_PLATFORM_TYPE),
  },
  {
    key: 'tradingServer',
    name: keyMap.sharedFieldsTradingServer,
  },
  {
    key: 'typeName',
    name: keyMap.sharedFieldsType,
  },
  {
    key: 'clientSourceType',
    name: keyMap.sharedFieldsClientSourceType,
    render: (row: ISalesUserTradingAccount, value: string) =>
      getValueFromEnums(value, ENUM_CONSTANTS.CLIENT_SOURCE_TYPE),
  },
  {
    key: 'agentCode',
    name: keyMap.sharedFieldsParentIbCode,
  },
  {
    key: 'currency',
    name: keyMap.sharedFieldsCurrency,
    render: (row: ISalesUserTradingAccount, value: string) => getValueFromEnums(value, ENUM_CONSTANTS.PSP_CURRENCY),
  },
  {
    key: 'balance',
    name: keyMap.commonBalance,
    render: (row: ISalesUserTradingAccount, value: number) => getFormattedNumber(value, { precision: true }),
  },
  {
    key: 'credit',
    name: keyMap.commonCredit,
    render: (row: ISalesUserTradingAccount, value: number) => getFormattedNumber(value, { precision: true }),
  },
  {
    key: 'equity',
    name: keyMap.commonEquity,
    render: (row: ISalesUserTradingAccount, value: number) => getFormattedNumber(value, { precision: true }),
  },
  {
    key: 'margin',
    name: keyMap.commonMargin,
    render: (row: ISalesUserTradingAccount, value: number) => getFormattedNumber(value, { precision: true }),
  },
  {
    key: 'marginFree',
    name: keyMap.commonFreeMargin,
    render: (row: ISalesUserTradingAccount, value: number) => getFormattedNumber(value, { precision: true }),
  },
  {
    key: 'leverage',
    name: keyMap.commonLeverage,
  },
  {
    key: 'enabled',
    name: keyMap.sharedFieldsEnabled,
    render: (row: ISalesUserTradingAccount, value: boolean) => getBooleanValue(value),
  },
  {
    key: 'readOnly',
    name: keyMap.salesFieldsTradingAccountIsReadOnly,
    render: (row: ISalesUserTradingAccount, value: boolean) => getBooleanValue(value),
  },
  {
    key: 'lastDepositActionOn',
    name: keyMap.salesFieldsTradingAccountLastDeposit,
    render: (row: ISalesUserTradingAccount, value: string) => {
      return <FormattedDateTime value={value} />;
    },
  },
  {
    key: 'lastLoginOn',
    name: keyMap.salesFieldsTradingAccountLastLogin,
    render: (row: ISalesUserTradingAccount, value: string) => {
      return <FormattedDateTime value={value} />;
    },
  },
];
